import dayjs from 'dayjs';

import { AssistantChatMessage, ChatSuggestedEvent, UnifiedChatRichContextEntry } from '../../types';
import { energySummaryMockHeatmap } from './energySummaryMockHeatmap';
import { ScheduleEvent } from '../../api/science/openapi/api';
import { transformResultJsonSchedulesToSuggestedEvents } from '../../helpers/resultJsonSchedule.helpers';

const atTime = (hour: number, minute: number) =>
  dayjs().hour(hour).minute(minute).second(0).millisecond(0).toISOString();

/**
 * Dev-only sample data for the "Mock rich context" debug quick action —
 * one item per suggested-block type (task/routine/event), plus a workout
 * event with an embedded workout_plan richContext so the v4 rich card UI
 * (AssistantChatSuggestedBlocks / SuggestedEventBlock with in-card plan)
 * can be visually verified without a live AI backend response.
 */
export const buildDebugMockSuggestedBlocks = (): ChatSuggestedEvent[] => {
  const today = dayjs().format('YYYY-MM-DD');

  const task: ChatSuggestedEvent = {
    eventId: 'debug-mock-task-1',
    name: 'Finish the Q4 deck',
    date: today,
    startTime: atTime(15, 0),
    endTime: atTime(16, 0),
    durationMin: 60,
    description:
      'Pull the latest revenue numbers from the finance dashboard and update slides 4–9 before tomorrow’s leadership review.',
    categoryId: 'default_deepWork',
    action: 'setTask',
  };

  const routine: ChatSuggestedEvent = {
    eventId: 'debug-mock-routine-1',
    name: 'Morning Routine',
    date: today,
    startTime: atTime(6, 30),
    endTime: atTime(7, 15),
    durationMin: 45,
    description: 'Stretch, hydrate, and review today’s top 3 priorities before checking email.',
    categoryId: 'default_exercise',
    recurring: {
      monday: { enabled: true },
      tuesday: { enabled: false },
      wednesday: { enabled: true },
      thursday: { enabled: false },
      friday: { enabled: true },
      saturday: { enabled: false },
      sunday: { enabled: false },
    },
    action: 'setRoutine',
  };

  // Update: has a `before` (old time), so the header shows the struck-through
  // old range under the new one and the CTA reads "Update Event".
  const eventUpdate: ChatSuggestedEvent = {
    eventId: 'debug-mock-event-1',
    name: 'Product sync w/ design',
    date: today,
    startTime: atTime(11, 0),
    endTime: atTime(11, 45),
    durationMin: 45,
    description:
      'Walk through the v4 chat card redesign, confirm the rich-context field list, and agree on the Figma handoff checklist.',
    categoryId: 'default_mtg',
    location: 'Zoom',
    before: {
      date: today,
      startTime: atTime(10, 0),
      endTime: atTime(10, 45),
    },
    action: 'setEvent',
  };

  // Create: no `before`, so the CTA reads "Create Event" and there's no
  // struck-through old time.
  const eventCreate: ChatSuggestedEvent = {
    eventId: 'debug-mock-event-2',
    name: 'Team happy hour',
    date: today,
    startTime: atTime(17, 30),
    endTime: atTime(18, 30),
    durationMin: 60,
    description: 'Celebrate the sprint wrap with the team at the rooftop bar down the block.',
    categoryId: 'default_social',
    location: 'The Rooftop',
    isNew: true,
    action: 'setEvent',
  };

  // Workout event with an embedded workout_plan richContext — verifies the
  // plan renders inside AssistantChatSuggestedEventBlock (padded card, no
  // standalone "Add to Calendar" / "Update Workout" CTAs; the block's own
  // accept CTA is enough).
  const workoutWithPlan: ChatSuggestedEvent = {
    eventId: 'debug-mock-workout-plan-1',
    name: '30-Min Strength Training',
    date: today,
    startTime: atTime(18, 0),
    endTime: atTime(18, 30),
    durationMin: 30,
    description: 'Strength session slotted after work, before your energy dips for the evening.',
    categoryId: 'default_exercise',
    isNew: true,
    action: 'setEvent',
    autoScheduleFrom: {
      from: 'workout',
      id: 'debug-mock-workout-plan-1',
      predicted: true,
    },
    richContext: {
      signals: [],
      location: null,
      action: null,
      reminder_min: null,
      enrichment_pending: false,
      content: {
        type: 'workout_plan',
        payload: {
          plan_title: '30-Min Strength Training',
          workout_type: 'gym',
          difficulty: 'intermediate',
          duration_min: 30,
          scheduled_start_time: '18:00',
          scheduled_end_time: '18:30',
          exercises: [
            { name: 'Push-ups', sets: 3, reps: 12, rest_sec: 45 },
            { name: 'Dumbbell rows', sets: 3, reps: 10, rest_sec: 45 },
            { name: 'Overhead press', sets: 3, reps: 10, rest_sec: 60 },
            { type: 'note', text: 'Finish with 5 min of stretching.' },
          ],
        },
      },
    },
  };

  return [task, routine, eventUpdate, eventCreate, workoutWithPlan];
};

/**
 * Dev-only sample `richContexts` — one query-sourced entry per rich-context
 * card type ContentCard.tsx supports (find_location, workout_plan,
 * workout_summary, meal_suggestion, meal_content, checklist, transit_route,
 * energy_summary, sleep_summary, hr_summary, metric_bar, audio_player,
 * journal_prompts, meal_plan_week), shaped per the science OpenAPI models
 * (NearbyPlace, WorkoutPlanPayload, MealSuggestionPayload,
 * SleepSummaryPayload, HRSummaryPayload — see
 * src/data-access/api/science/openapi/api.ts) so
 * AssistantChatRichContextCard renders the same cards a live v3 chat
 * response would produce.
 */
export const buildDebugMockRichContexts = (): UnifiedChatRichContextEntry[] => {
  const today = dayjs().format('YYYY-MM-DD');

  const findLocation: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-location',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'find_location',
        payload: {
          query: 'coffee shops near the office',
          nearby_places: [
            {
              name: 'Blue Bottle Coffee',
              category: 'cafe',
              lat: 37.7749,
              lng: -122.4194,
              distance_m: 320,
              walk_min: 4,
              map_url: 'https://maps.google.com/?q=Blue+Bottle+Coffee',
              opening_hours: '7:00 AM – 6:00 PM',
              rating: 4.5,
            },
            {
              name: 'Ritual Coffee Roasters',
              category: 'cafe',
              lat: 37.7755,
              lng: -122.4183,
              distance_m: 540,
              walk_min: 7,
              map_url: 'https://maps.google.com/?q=Ritual+Coffee+Roasters',
              opening_hours: '6:30 AM – 7:00 PM',
              rating: 4.6,
            },
          ],
        },
      },
      reminder_min: null,
    },
  };

  const workoutPlan: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-workout',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'workout_plan',
        payload: {
          plan_title: '30-Min Upper Body Strength',
          workout_type: 'gym',
          difficulty: 'intermediate',
          duration_min: 30,
          exercises: [
            { name: 'Push-ups', sets: 3, reps: 12, rest_sec: 45 },
            { name: 'Dumbbell rows', sets: 3, reps: 10, rest_sec: 45 },
            { name: 'Overhead press', sets: 3, reps: 10, rest_sec: 60 },
            { type: 'note', text: 'Finish with 5 min of stretching.' },
          ],
        },
      },
      reminder_min: 15,
    },
  };

  const mealSuggestion: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-meal',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'meal_suggestion',
        payload: {
          type: 'lunch',
          title: 'High-protein grain bowl',
          description:
            'Grilled chicken, quinoa, roasted vegetables, and tahini dressing — balanced for an afternoon focus block.',
          food_items: [
            { name: 'Grilled chicken breast', amount: 150 },
            { name: 'Quinoa', amount: 180 },
            { name: 'Roasted vegetables', amount: 120 },
          ],
          start_time: atTime(12, 30),
          end_time: atTime(13, 15),
        },
      },
      reminder_min: 10,
    },
  };

  const sleepSummary: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-sleep',
    source: 'query',
    richContext: {
      signals: [
        {
          signal_source: 'sleep',
          urgency: 'info',
          body: 'You slept 7.2h with 88% efficiency.',
          expires_at: null,
          data: null,
        },
      ],
      location: null,
      action: null,
      content: {
        type: 'sleep_summary',
        payload: {
          duration_h: 7.2,
          quality_score: 82,
          efficiency: 88,
          sleep_time: atTime(23, 10),
          awake_time: atTime(6, 25),
          heart_rate_summary: {
            avg_hr_bpm: 58,
            resting_hr_bpm: 52,
            min_hr_bpm: 48,
            max_hr_bpm: 64,
            avg_hrv_rmssd: 42,
          },
          suggested_sleep: {
            start_time: atTime(22, 45),
            end_time: atTime(6, 45),
            duration_min: 480,
            reason: 'Matches your usual 8h recovery window.',
          },
        },
      },
      reminder_min: null,
    },
  };

  const hrSummary: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-hr',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'hr_summary',
        payload: {
          date: today,
          avg_hr_bpm: 71,
          min_hr_bpm: 54,
          max_hr_bpm: 132,
          timeline: [
            { timestamp: atTime(7, 0), value: 62 },
            { timestamp: atTime(9, 0), value: 78 },
            { timestamp: atTime(12, 0), value: 88 },
            { timestamp: atTime(15, 0), value: 75 },
            { timestamp: atTime(18, 0), value: 95 },
          ],
        },
      },
      reminder_min: null,
    },
  };

  const workoutSummary: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-workout-summary',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'workout_summary',
        payload: {
          workout_type: 'running',
          duration_min: 32,
          steps: 4820,
          calories: 310,
          distance_meters: 5200,
          source: 'Apple Watch',
        },
      },
      reminder_min: null,
    },
  };

  const mealContent: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-meal-content',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'meal_content',
        payload: {
          dish_name: 'Chicken teriyaki bowl',
          venue_name: 'Sunrise Kitchen',
          rating: 4.3,
          dish_tags: ['high-protein', 'gluten-free'],
          macros: { protein_g: 38, carbohydrates_g: 52, fat_g: 14 },
          meal_details: [
            {
              type: 'lunch',
              title: 'Chicken teriyaki bowl',
              startTime: atTime(12, 30),
              endTime: atTime(13, 0),
              food_items: [
                { name: 'Grilled chicken', amount: 150 },
                { name: 'Steamed rice', amount: 180 },
                { name: 'Broccoli', amount: 90 },
              ],
            },
          ],
        },
      },
      reminder_min: null,
    },
  };

  const checklist: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-checklist',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'checklist',
        payload: {
          label: 'Before you head out',
          items: [
            { text: 'Refill water bottle', done: true },
            { text: 'Charge the Apple Watch', done: true },
            { text: 'Pack gym clothes', done: false },
          ],
        },
      },
      reminder_min: null,
    },
  };

  const transitRoute: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-transit',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'transit_route',
        payload: {
          origin: 'Home',
          destination: 'Office',
          depart_by: '8:20 AM',
        },
      },
      reminder_min: 5,
    },
  };

  const energySummary: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-energy',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'energy_summary',
        payload: {
          current_zone: 'peak',
          heatmap: energySummaryMockHeatmap.map((point) => ({
            x: 'dummy-same',
            y: `${today}T${point.time}:00`,
            value: point.value,
          })),
          zones: [
            { label: 'Peak', level: 'peak', start: '09:00', end: '12:00' },
            { label: 'Calm', level: 'calm', start: '12:00', end: '15:00' },
            { label: 'Low', level: 'low', start: '15:00', end: '17:00' },
            { label: 'Recovering', level: 'recovering', start: '17:00', end: '21:00' },
          ],
        },
      },
      reminder_min: null,
    },
  };

  const metricBar: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-metric-bar',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'metric_bar',
        payload: {
          label: 'Readiness score',
          value: 78,
          unit: '%',
          min: 0,
          max: 100,
          baseline: 65,
          color_ramp: 'red_to_green',
        },
      },
      reminder_min: null,
    },
  };

  const audioPlayer: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-audio',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'audio_player',
        payload: {
          track_name: 'Deep Focus — 40Hz Binaural',
          category: 'focus',
          duration_min: 25,
          stream_url: null,
        },
      },
      reminder_min: null,
    },
  };

  const journalPrompts: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-journal',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'journal_prompts',
        payload: {
          label: 'Evening wind-down',
          prompts: [
            'What’s one thing that went well today?',
            'What’s still on your mind that you can let go of until tomorrow?',
            'What’s the first thing you want to focus on tomorrow morning?',
          ],
        },
      },
      reminder_min: null,
    },
  };

  const mealPlanWeek: UnifiedChatRichContextEntry = {
    date: today,
    suggestionId: 'debug-mock-query-meal-plan-week',
    source: 'query',
    richContext: {
      signals: [],
      location: null,
      action: null,
      content: {
        type: 'meal_plan_week',
        payload: {
          days: [
            {
              date: today,
              meals: [
                { slot: 'breakfast', dish: 'Greek yogurt bowl', kcal: 320, time: '07:30' },
                { slot: 'lunch', dish: 'High-protein grain bowl', kcal: 540, time: '12:30' },
                { slot: 'dinner', dish: 'Grilled salmon & greens', kcal: 610, time: '19:00' },
              ],
            },
            {
              date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
              meals: [
                { slot: 'breakfast', dish: 'Oatmeal & berries', kcal: 300, time: '07:30' },
                { slot: 'lunch', dish: 'Turkey wrap', kcal: 480, time: '12:30' },
                { slot: 'dinner', dish: 'Stir-fry tofu & veggies', kcal: 520, time: '19:00' },
              ],
            },
          ],
        },
      },
      reminder_min: null,
    },
  };

  return [
    findLocation,
    workoutPlan,
    mealSuggestion,
    sleepSummary,
    hrSummary,
    workoutSummary,
    mealContent,
    checklist,
    transitRoute,
    energySummary,
    metricBar,
    audioPlayer,
    journalPrompts,
    mealPlanWeek,
  ];
};

/**
 * Dev-only sample data for the "Mock chat w/ replan result" debug quick
 * action — a trimmed "result_json" SSE payload (meal, sleep, and todo
 * schedule items, matching what the general chat stream's `result_json`
 * event carries — see transformResultJsonSchedulesToSuggestedEvents) run
 * through that same mapper, so AssistantChatSuggestedEventBlock can be
 * visually verified against a real backend shape without a live AI backend.
 */
export const buildDebugMockResultJsonSchedules = (): ChatSuggestedEvent[] => {
  const today = dayjs().format('YYYY-MM-DD');

  const rawSchedules: ScheduleEvent[] = [
    {
      name: 'Breakfast',
      start_time: '08:00',
      end_time: '10:00',
      event_from: 'meal',
      category: 'Meal',
      action_id: 'debug-mock-result-json-breakfast',
      explanation: 'Breakfast at 8:00am–10:00am supports your energy and nutrition goals.',
      rich_context: {
        signals: [],
        location: null,
        action: null,
        content: {
          type: 'meal_suggestion',
          payload: {
            type: 'breakfast',
            title: 'Hearty Breakfast Bowl',
            description:
              'Start your day with a nourishing breakfast that fuels your morning energy peak.',
            food_items: [
              { name: 'Oatmeal', amount: 50, unit: 'g' },
              { name: 'Almond milk', amount: 200, unit: 'ml' },
              { name: 'Banana', amount: 1, unit: 'piece' },
              { name: 'Chia seeds', amount: 15, unit: 'g' },
            ],
            macros: { calories: 350, fat_g: 7, carbohydrates_g: 65, protein_g: 10 },
          },
        },
      } as any,
      start_date: today,
      end_date: today,
      start_day_offset: 0,
      end_day_offset: 0,
    },
    {
      name: 'Sleep',
      start_time: '00:00',
      end_time: '07:00',
      event_from: 'sleep',
      category: 'Other',
      action_id: 'debug-mock-result-json-sleep',
      explanation: 'Sleep from 12:00am–7:00am targets your recovery with a full rest cycle.',
      start_date: today,
      end_date: today,
      start_day_offset: 0,
      end_day_offset: 0,
    },
    {
      name: 'Finish the Q4 deck',
      start_time: '10:00',
      end_time: '11:30',
      duration: 90,
      category: 'light_task',
      event_from: 'todo',
      origin_todo_id: 'debug-mock-result-json-origin-todo',
      explanation: 'Deep-work block during your peak energy window.',
      action_id: 'debug-mock-result-json-todo',
      start_date: today,
      end_date: today,
      start_day_offset: 0,
      end_day_offset: 0,
    },
    {
      name: 'Morning Stretch',
      start_time: '07:15',
      end_time: '07:30',
      duration: 15,
      category: 'routine',
      event_from: 'routine',
      origin_routine_activity_id: 'debug-mock-result-json-origin-routine',
      explanation: 'Your usual morning routine, right after waking up.',
      action_id: 'debug-mock-result-json-routine',
      start_date: today,
      end_date: today,
      start_day_offset: 0,
      end_day_offset: 0,
    },
    {
      name: '30-Min Strength Training',
      start_time: '17:30',
      end_time: '18:00',
      duration: 30,
      category: 'workout',
      event_from: 'workout',
      action_id: 'debug-mock-result-json-workout',
      explanation: 'Strength session slotted after work, before your energy dips for the evening.',
      rich_context: {
        signals: [],
        location: null,
        action: null,
        content: {
          type: 'workout_plan',
          payload: {
            plan_title: '30-Min Strength Training',
            workout_type: 'gym',
            difficulty: 'intermediate',
            duration_min: 30,
            exercises: [
              { name: 'Push-ups', sets: 3, reps: 12, rest_sec: 45 },
              { name: 'Dumbbell rows', sets: 3, reps: 10, rest_sec: 45 },
              { name: 'Overhead press', sets: 3, reps: 10, rest_sec: 60 },
              { type: 'note', text: 'Finish with 5 min of stretching.' },
            ],
          },
        },
      } as any,
      start_date: today,
      end_date: today,
      start_day_offset: 0,
      end_day_offset: 0,
    },
    // adjustments.schedule item whose rich_context belongs on the calendar
    // card (not a second bubble). Mirrors a live general-chat result_json.
    {
      name: 'Workout - Chest, Arms, Back',
      start_time: '20:00',
      end_time: '20:30',
      action_id: 'debug-mock-result-json-chest-arms-back',
      category: 'exercise',
      event_from: 'activity',
      origin_todo_id: '',
      explanation: 'Scheduled during peak energy for optimal performance.',
      start_date: today,
      end_date: today,
      location: '',
      rich_context: {
        signals: [],
        location: null,
        reminder_min: null,
        action: {
          label: 'Start workout',
          action_type: 'start_timer',
          payload: {
            duration_min: 30,
            session_title: 'Workout',
            scheduled_start_time: '20:00',
            scheduled_end_time: '20:30',
          },
        },
        content: {
          type: 'workout_plan',
          payload: {
            plan_title: 'Intermediate Chest, Arms, and Back Workout',
            workout_type: 'gym',
            difficulty: 'intermediate',
            exercises: [
              { name: 'Bench Press', sets: 3, rest_sec: 60, reps: 10 },
              { name: 'Dumbbell Flyes', sets: 3, rest_sec: 60, reps: 12 },
              { name: 'Pull-Ups', sets: 3, rest_sec: 90, reps: 8 },
              { name: 'Bent Over Rows', sets: 3, rest_sec: 60, reps: 10 },
              { name: 'Tricep Dips', sets: 3, rest_sec: 60, reps: 10 },
              { name: 'Bicep Curls', sets: 3, rest_sec: 60, reps: 12 },
              { name: 'Face Pulls', sets: 3, rest_sec: 60, reps: 12 },
              { type: 'meta', estimated_calories: 300 },
            ],
            duration_min: 30,
            scheduled_start_time: '20:00',
            scheduled_end_time: '20:30',
          },
        },
        enrichment_pending: false,
      } as any,
      start_day_offset: 0,
      end_day_offset: 0,
    },
  ];

  return transformResultJsonSchedulesToSuggestedEvents(rawSchedules, today);
};

/**
 * Dev-only: a mock `question` message — the "time_conflict" CTA card
 * (ConversationQuestionCard) a live chat stream sends when a suggested time
 * overlaps an existing event, so that card can be visually verified without
 * a live AI backend hitting a real conflict.
 */
export const buildDebugMockConversationQuestionMessage = (): AssistantChatMessage => {
  const sentAt = new Date().toISOString();
  const suffix = Date.now();

  return {
    messageId: `debug_mock_question_${suffix}`,
    content: '',
    role: 'assistant',
    sentAt,
    isAnswered: false,
    question: {
      context: 'That time overlaps with **🍽️ Dinner** (20:15–21:15).',
      question: 'What would you like to do?',
      trigger: 'time_conflict',
      conflicting_event_ids: ['a6a50e646f2544d38714d38a3f59c5df'],
      conflicting_event_names: ['🍽️ Dinner'],
      options: [
        {
          action: 'proceed_anyway',
          label: 'Keep 20:00 (overlap OK)',
          payload: { event_id: 'a6a50e646f2544d38714d38a3f59c5df', requested_time: '20:00' },
          style: 'secondary',
        },
        {
          action: 'free_text_reply',
          label: 'Pick another time',
          payload: {},
          style: 'primary',
        },
      ],
    },
  };
};

/**
 * Dev-only: a full mock conversation — user turn, an assistant "action"
 * message (suggested task/routine/event blocks), a second user turn, an
 * assistant "suggestion" message (map/workout/meal/sleep/HR rich-context
 * cards), a third user turn with a "time_conflict" `question` message, and
 * a final turn that leaves a live `statusText` under the last bubble (v4
 * sequence.status loading indicator) — so all v4 bubble kinds can be
 * visually verified in sequence without a live AI backend.
 */
export const buildDebugMockConversationMessages = (): AssistantChatMessage[] => {
  const sentAt = new Date().toISOString();
  const suffix = Date.now();

  return [
    {
      messageId: `debug_mock_user_1_${suffix}`,
      content: 'Plan my day — add my top task, morning routine, and the design sync.',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_text_1_${suffix}`,
      content:
        'Here’s your plan for today — I added your top task, morning routine, and the design sync.',
      role: 'assistant',
      sentAt,
    },
    {
      messageId: `debug_mock_action_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      suggestedBlocks: buildDebugMockSuggestedBlocks(),
    },
    {
      messageId: `debug_mock_user_2_${suffix}`,
      content:
        'What’s my sleep and heart rate looking like, and suggest a workout plus a lunch spot nearby?',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_text_2_${suffix}`,
      content:
        'Here’s what I found — your sleep and heart rate summary, a workout to try, and a lunch spot nearby.',
      role: 'assistant',
      sentAt,
    },
    {
      messageId: `debug_mock_suggestion_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      richContexts: buildDebugMockRichContexts(),
    },
    {
      messageId: `debug_mock_user_3_${suffix}`,
      content: 'Add an evening break at 8pm.',
      role: 'user',
      sentAt,
    },
    // Same-turn regression case: an action (suggestedBlocks) message
    // immediately followed by a question message, no user message in
    // between — both must stay independently interactive (see ctaDisabled/
    // hasSubsequentAssistantMessages in AssistantChatContent).
    {
      messageId: `debug_mock_action_conflict_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      suggestedBlocks: [
        {
          eventId: 'debug-mock-conflict-event',
          name: 'Evening Break',
          date: dayjs().format('YYYY-MM-DD'),
          startTime: atTime(20, 0),
          endTime: atTime(20, 30),
          durationMin: 30,
          categoryId: 'default_break',
          isNew: true,
          action: 'setEvent',
        },
      ],
    },
    buildDebugMockConversationQuestionMessage(),
    // v4 loading: sequence.status under the last bubble. Two shapes —
    // (1) status attached to a finished text bubble, and (2) a status-only
    // placeholder with no card chrome — matching RN AssistantMessageItem's
    // SequenceStatusText row (no purple circle indicator).
    {
      messageId: `debug_mock_user_status_${suffix}`,
      content: 'Can you also free up tomorrow morning?',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_text_with_status_${suffix}`,
      content: 'Sure — I’ll clear a focus block tomorrow morning.',
      role: 'assistant',
      sentAt,
      statusText: 'Checking your calendar…',
    },
    {
      messageId: `debug_mock_status_only_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      statusText: 'Finding an open slot…',
    },
  ];
};

/**
 * Dev-only: canned Plan My Day loading status lines for the
 * `scheduler-chat-ui__status_messages` UI (same stack used by SchedulerChatUI
 * week/day planning). Injected via the debug quick action so the status
 * bubble can be visually verified without a live stream.
 */
export const DEBUG_MOCK_PLAN_STATUS_LINES = [
  'Preparing your weekly plan...',
  'Planning Monday...',
  'Planning Tuesday...',
  'Planning Wednesday...',
  'Planning all 3 days...',
] as const;

/**
 * Dev-only: a Plan My Day loading conversation — user turn + live
 * `allPlanPreview` bubble. Pair with `DEBUG_MOCK_PLAN_STATUS_LINES` on
 * `suggestAllAIPlanState` so PlanPreviewBubble renders
 * `scheduler-chat-ui__status_messages`.
 */
export const buildDebugMockPlanStatusConversationMessages = (): AssistantChatMessage[] => {
  const sentAt = new Date().toISOString();
  const suffix = Date.now();

  return [
    {
      messageId: `debug_mock_plan_status_user_${suffix}`,
      content: 'Plan my day',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_plan_status_preview_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      allPlanPreview: true,
    },
  ];
};

/**
 * Dev-only: a mock "Replan" conversation — user turn, a summary text bubble,
 * and an assistant "action" message built from a result_json-shaped schedule
 * list (meal/sleep/todo/workout). Schedule rich context (the chest workout
 * plan) sits on that calendar card. A later query-sourced rich context is its
 * own bubble.
 */
export const buildDebugMockReplanConversationMessages = (): AssistantChatMessage[] => {
  const sentAt = new Date().toISOString();
  const suffix = Date.now();
  const today = dayjs().format('YYYY-MM-DD');

  return [
    {
      messageId: `debug_mock_replan_user_${suffix}`,
      content: 'Replan my day.',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_replan_summary_${suffix}`,
      content:
        "I've scheduled 2 of your tasks and added 1 additional activity to optimize your day.",
      role: 'assistant',
      sentAt,
    },
    {
      messageId: `debug_mock_replan_action_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      suggestedBlocks: buildDebugMockResultJsonSchedules(),
    },
    {
      messageId: `debug_mock_replan_user_query_${suffix}`,
      content: 'Any coffee shops near the office?',
      role: 'user',
      sentAt,
    },
    {
      messageId: `debug_mock_replan_query_${suffix}`,
      content: '',
      role: 'assistant',
      sentAt,
      richContexts: [
        {
          date: today,
          suggestionId: 'debug-mock-replan-query-location',
          source: 'query',
          richContext: {
            signals: [],
            location: null,
            action: null,
            content: {
              type: 'find_location',
              payload: {
                query: 'coffee shops near the office',
                nearby_places: [
                  {
                    name: 'Blue Bottle Coffee',
                    category: 'cafe',
                    lat: 37.7749,
                    lng: -122.4194,
                    distance_m: 320,
                    walk_min: 4,
                    map_url: 'https://maps.google.com/?q=Blue+Bottle+Coffee',
                  },
                ],
              },
            },
            reminder_min: null,
          },
        },
      ],
    },
  ];
};
